import {
  DuplicateContactsSelectionCancelledError,
  useResolveDuplicatesBeforeAdding,
} from '@components/duplicate-contacts-modal/useResolveDuplicatesBeforeAdding';
import {
  checkContactsUniqueness,
  createContact,
  getContact,
  getContactLists,
  getContactsTopics,
  getExistingContacts,
  removeContactFromMediaLists,
  requestContactUpdate,
  updateContact,
} from '@contact/services/contactsService';
import { Contact } from '@contact/types/contact';
import { CreateContactPayload } from '@contact/types/contacts';
import { getIsMatchingExistingContactsQuery } from '@contact/utils/queries';
import { queryClient } from '@lib/tanstack-query';
import { trackGaEvent } from '@lib/trackGaEvent';
import { MAILING_ANALYTICS_SENT_RECIPIENTS_BASE_KEY } from '@mailings/hooks/queries/mailingsQueries';
import {
  MEDIA_LIST_CONTACTS_BASE_KEYS,
  MEDIA_LIST_LIST_INFINITE_KEYS,
  MEDIA_LIST_BASE_KEY,
} from '@media-lists/hooks/mediaListsQueries';
import { useNotifications } from '@src/components/Notifications';
import { ListPagination } from '@src/types/api-types';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { useTranslation } from 'react-i18next';

export const CONTACT_BASE_KEY = 'contact';
export const CONTACT_EXISTING_KEY = ['contacts', 'existing'];

const GET_CONTACT_MAX_FAILURE_COUNT = 3;

const contactsKeys = {
  contact: (contactId: number) =>
    queryOptions<Contact, AxiosError>({
      queryKey: [CONTACT_BASE_KEY, contactId],
      queryFn: () => getContact(contactId),
      retry: (failureCount, error) => {
        if (error.response?.status === HttpStatusCode.NotFound) {
          return false;
        }

        return failureCount < GET_CONTACT_MAX_FAILURE_COUNT;
      },
    }),
  contactLists: (contactId: number) =>
    infiniteQueryOptions({
      queryKey: [CONTACT_BASE_KEY, contactId, 'media-lists'],
      queryFn: ({ pageParam }) => getContactLists(contactId, pageParam),
      initialPageParam: 1,
      refetchOnWindowFocus: false,
      getNextPageParam: ({ pagination }: { pagination?: ListPagination }) => {
        if (!pagination?.currentPage || !pagination.pageItems || !pagination.totalCount) {
          return undefined;
        }
        return pagination.currentPage * pagination.pageItems < pagination.totalCount
          ? pagination.currentPage + 1
          : undefined;
      },
    }),
  contactsTopics: (search?: string) =>
    queryOptions({
      queryKey: ['contacts', 'topics', search],
      refetchOnMount: false,
      queryFn: () => getContactsTopics(search),
      placeholderData: keepPreviousData,
    }),
  existingContacts: (sourceUids: string[]) =>
    queryOptions({
      queryKey: [...CONTACT_EXISTING_KEY, sourceUids],
      queryFn: () => getExistingContacts({ sourceUids }),
      placeholderData: keepPreviousData,
      retry: false,
    }),
};

export const useCreateContactMutation = (onSuccess?: () => void, onCancel?: () => void) => {
  const { t } = useTranslation(['contactForm']);
  const queryClient = useQueryClient();
  const { showErrorNotification, showSuccessNotification } = useNotifications();
  const resolveDuplicatesBeforeAdding = useResolveDuplicatesBeforeAdding();

  const completeCreateContactSuccess = () => {
    onSuccess?.();
    showSuccessNotification(t('contactForm:createContactSuccess'));
    void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_CONTACTS_BASE_KEYS });
    void queryClient.invalidateQueries({ queryKey: [MEDIA_LIST_BASE_KEY] });
  };

  return useMutation({
    mutationFn: async (payload: CreateContactPayload) => {
      const pendingContactId = `new-contact:${payload.email.trim().toLowerCase()}`;
      const decision = await resolveDuplicatesBeforeAdding({
        contactsBeingAdded: [
          {
            id: pendingContactId,
            name: `${payload.firstName} ${payload.lastName}`.trim() || payload.email,
            email: payload.email,
          },
        ],
        mediaListsId: payload.smartGroupIds,
      });

      if (decision.isCancelled || decision.contactIdsToSkip.has(pendingContactId)) {
        onCancel?.();
        throw new DuplicateContactsSelectionCancelledError();
      }

      return createContact(payload);
    },
    onSuccess: () => {
      completeCreateContactSuccess();
    },
    onError: (error) => {
      if (error instanceof DuplicateContactsSelectionCancelledError) {
        return;
      }

      showErrorNotification(t('contactForm:createContactFailure'));
    },
  });
};

export const useContactQuery = (
  contactId: number,
  options?: Omit<UseQueryOptions<Contact, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    ...contactsKeys.contact(contactId),
    ...options,
  });
};

export const useContactListsQuery = (contactId: number, options?: { enabled?: boolean }) => {
  return useInfiniteQuery({ ...contactsKeys.contactLists(contactId), ...options });
};

export const useContactMutation = () => {
  const { t } = useTranslation(['contactForm']);
  const queryClient = useQueryClient();
  const { showErrorNotification, showSuccessNotification } = useNotifications();

  return useMutation({
    mutationFn: updateContact,
    onSuccess: (data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_CONTACTS_BASE_KEYS });
      queryClient.setQueryData(contactsKeys.contact(id).queryKey, data);
      void queryClient.invalidateQueries({
        queryKey: [MAILING_ANALYTICS_SENT_RECIPIENTS_BASE_KEY],
      });
      showSuccessNotification(t('contactForm:editContactSuccess'));
      trackGaEvent('add_item', {
        item_label: 'add_contacts_to_media_list',
        event_source: 'form',
      });
    },
    onError: () => {
      showErrorNotification(t('contactForm:editContactFailure'));
    },
  });
};

export const useContactsUniquenessMutation = () => {
  return useMutation({
    mutationFn: checkContactsUniqueness,
  });
};

export const useExistingContacts = (sourceUids: string[]) => {
  return useQuery(contactsKeys.existingContacts(sourceUids));
};

export const invalidateExistingContacts = (sourceUid?: string) => {
  if (!sourceUid) {
    return queryClient.invalidateQueries({ queryKey: CONTACT_EXISTING_KEY });
  }

  return queryClient.invalidateQueries({
    predicate: (query) => {
      return getIsMatchingExistingContactsQuery(query.queryKey, sourceUid);
    },
  });
};

export const useRequestContactUpdate = () => {
  return useMutation({
    mutationFn: requestContactUpdate,
  });
};

export const useRemoveFromAllMediaListsMutation = () => {
  const { t } = useTranslation(['contact']);
  const { showErrorNotification, showSuccessNotification } = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeContactFromMediaLists,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_CONTACTS_BASE_KEYS });
      void queryClient.invalidateQueries({ queryKey: MEDIA_LIST_LIST_INFINITE_KEYS });
      showSuccessNotification(t('contact:removeFromAllLists.success'));
    },
    onError: () => {
      showErrorNotification(t('contact:removeFromAllLists.failure'));
    },
  });
};
